<template>
  <b-card header-tag="header" footer-tag="footer">
    <template v-slot:header>
      <h6 class="mb-0">Screensaver</h6> 
    </template>
    
    <b-alert show variant="warning" v-if="current_screensaver==null">There is no available screensaver for this device yet</b-alert>
    <br>
    <b-alert show variant="success" v-if="current_screensaver!=null">There is a screensaver available, you can continue the configuration directly on the device (created on {{current_screensaver.created_at | formatDate}})<b-badge class="mr-1" variant="success" ></b-badge></b-alert>
    
    <br>
    <p>
      For the screensaver feature to work on your Sfir device you will need to upload one JPG to appear in the screen of your device, when no activity is detected. 
      If you upload more than 1 JPG picture, all those images will be rendered as animations, one after the other.
    </p>
    <p>
      <strong>You can also upload a GIF file</strong> — the frames will be automatically extracted, resized &amp; center-cropped to fit the screen, and converted to JPG for you.
    </p>

    <!-- Size selector for sfir_cap -->
    <div v-if="isSfirCap" class="mb-3">
      <label class="font-weight-bold">Screen resolution:</label>
      <b-form-radio-group v-model="selectedSize" :options="sizeOptions" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
    </div>

    <b-tabs content-class="mt-3" v-model="uploadTab">
      <b-tab title="Upload GIF" active>
        <b-form-file
          accept=".gif, .GIF"
          v-model="gif_file"
          placeholder="Choose a GIF file..."
          drop-placeholder="Drop GIF here..."
        >
          <template slot="file-name" slot-scope="{ names }">
            <b-badge variant="dark">{{ names[0] }}</b-badge>
          </template>
        </b-form-file>
        <div v-if="processing" class="mt-3">
          <b-spinner small variant="primary"></b-spinner>
          <span class="ml-2">Processing GIF: {{ processingProgress }}</span>
        </div>
        <div v-if="gif_frames_ready" class="mt-3">
          <b-alert show variant="info">
            <strong>{{ extracted_files.length }}</strong> frames extracted and fitted to {{ targetWidth }}×{{ targetHeight }} (quality: {{ finalQuality }}%).
          </b-alert>
          <div v-if="preview_frames.length > 0" class="text-center">
            <p class="mb-1"><small>Preview:</small></p>
            <img :src="preview_frames[previewIndex]" alt="Animation preview" :style="{ width: targetWidth / 2 + 'px', height: targetHeight / 2 + 'px', border: '1px solid #ccc', borderRadius: '4px' }" />
          </div>
        </div>
      </b-tab>
      <b-tab title="Upload JPGs">
        <b-form-file
          multiple
          accept=".jpg, .JPG, .jpeg, .JPEG"
          v-model="files_to_upload"
        >
          <template slot="file-name" slot-scope="{ names }">
            <b-badge variant="dark">{{ names[0] }}</b-badge>
            <b-badge v-if="names.length > 1" variant="dark" class="ml-1">
              + {{ names.length - 1 }} More files
            </b-badge>
          </template>
        </b-form-file>
      </b-tab>
    </b-tabs>

    <br />
    <div class="float-right">
      <br />
      <b-button
        ref="upload_btn"
        variant="danger"
        style="float-right"
        :disabled="!canUpload"
        @click="uploadAttempt()"
      ><b-spinner small v-show="shouldBeSpinning()"></b-spinner> Upload</b-button>
    </div>
  </b-card>
</template>

<script>
import { parseGIF, decompressFrames } from "gifuct-js";
import { UPDATE_SCREENSAVER, GET_SCREENSAVER } from "@/core/services/store/devices.module";

const MAX_FRAME_SIZE = 8.5 * 1024; // 8.5 KB
const INITIAL_QUALITY = 1.0;
const QUALITY_STEP = 0.05;
const MIN_QUALITY = 0.05;
const MAX_FRAMES = 60;

// Reduce frames to MAX_FRAMES using progressive dropping:
// 1) Drop 1 every 4 (keep 75%)
// 2) Drop 1 every 3 (keep 66%)
// 3) Drop 1 every 2 (keep 50%)
// 4) If still over, truncate to MAX_FRAMES
function reduceFrames(frames) {
  if (frames.length <= MAX_FRAMES) return frames;
  for (const dropEvery of [4, 3, 2]) {
    const reduced = frames.filter((_, i) => i % dropEvery !== dropEvery - 1);
    if (reduced.length <= MAX_FRAMES) return reduced;
    // If this pass helped but not enough, keep reducing from the result
    if (reduced.length < frames.length) {
      frames = reduced;
    }
  }
  // Still over: hard cap at MAX_FRAMES
  return frames.slice(0, MAX_FRAMES);
}

export default {
  name: "suitch-screensaver-widget",
  components: {},
  props: {
    device: null,
  },
  mounted() {
    this.downloadData();
  },
  data() {
    return {
      uploadTab: 0,
      selectedSize: "240x320",
      files_to_upload: null,
      gif_file: null,
      extracted_files: [],
      gif_frames_ready: false,
      preview_frames: [],
      previewIndex: 0,
      previewTimer: null,
      processing: false,
      processingProgress: "",
      finalQuality: 0,
      uploading: false,
      current_device: { connection_type: -1 },
      current_screensaver: null,
    };
  },
  computed: {
    isSfirCap() {
      return this.device && this.device.version === "sfir_cap";
    },
    sizeOptions() {
      return [
        { text: "240 × 320", value: "240x320" },
        { text: "320 × 480", value: "320x480" },
      ];
    },
    targetWidth() {
      return this.selectedSize === "320x480" ? 320 : 240;
    },
    targetHeight() {
      return this.selectedSize === "320x480" ? 480 : 320;
    },
    canUpload() {
      if (this.uploading || this.processing) return false;
      if (this.uploadTab === 0) return this.gif_frames_ready && this.extracted_files.length > 0;
      return this.files_to_upload != null && this.files_to_upload.length > 0;
    },
  },
  methods: {
    shouldBeSpinning() {
      return this.uploading;
    },

    // Scale source to cover target, then center-crop to exact target size
    drawCoverCrop(srcCanvas, outCtx, targetW, targetH) {
      const srcW = srcCanvas.width;
      const srcH = srcCanvas.height;
      // Scale factor so the source fully covers the target area
      const scale = Math.max(targetW / srcW, targetH / srcH);
      const scaledW = srcW * scale;
      const scaledH = srcH * scale;
      // Center the scaled image and let the overflow be cropped
      const offsetX = (targetW - scaledW) / 2;
      const offsetY = (targetH - scaledH) / 2;
      // Fill with white instead of clearRect to avoid transparent regions
      outCtx.fillStyle = "#FFFFFF";
      outCtx.fillRect(0, 0, targetW, targetH);
      outCtx.drawImage(srcCanvas, 0, 0, srcW, srcH, offsetX, offsetY, scaledW, scaledH);
    },

    // Convert canvas to JPG blob, reducing quality until < MAX_FRAME_SIZE
    async canvasToConstrainedBlob(canvas, startQuality) {
      let quality = startQuality;
      while (quality >= MIN_QUALITY) {
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", quality)
        );
        if (blob.size <= MAX_FRAME_SIZE) {
          return { blob, quality };
        }
        quality -= QUALITY_STEP;
      }
      // Last resort: use minimum quality
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", MIN_QUALITY)
      );
      return { blob, quality: MIN_QUALITY };
    },

    async processGif(file) {
      this.processing = true;
      this.gif_frames_ready = false;
      this.extracted_files = [];
      this.stopPreview();
      this.preview_frames = [];
      this.finalQuality = 0;
      this.processingProgress = "reading file...";
      this.$forceUpdate();

      const tw = this.targetWidth;
      const th = this.targetHeight;

      try {
        const arrayBuffer = await file.arrayBuffer();
        this.processingProgress = "parsing GIF...";
        this.$forceUpdate();

        const gif = parseGIF(arrayBuffer);
        const allFrames = decompressFrames(gif, true);

        if (allFrames.length === 0) {
          this.processing = false;
          this.$forceUpdate();
          return;
        }

        const frames = reduceFrames(allFrames);
        if (allFrames.length > frames.length) {
          this.processingProgress = `reduced from ${allFrames.length} to ${frames.length} frames...`;
          this.$forceUpdate();
          await new Promise((r) => setTimeout(r, 0));
        }

        if (frames.length === 0) {
          this.processing = false;
          this.$forceUpdate();
          return;
        }

        // Compositing canvas at original GIF dimensions
        const gifWidth = frames[0].dims.width;
        const gifHeight = frames[0].dims.height;
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = gifWidth;
        tempCanvas.height = gifHeight;
        const tempCtx = tempCanvas.getContext("2d");
        // Fill with white to replace any transparency
        tempCtx.fillStyle = "#FFFFFF";
        tempCtx.fillRect(0, 0, gifWidth, gifHeight);

        // Output canvas at target resolution
        const outCanvas = document.createElement("canvas");
        outCanvas.width = tw;
        outCanvas.height = th;
        const outCtx = outCanvas.getContext("2d");

        // Determine quality from first frame, then reuse for all frames
        let decidedQuality = null;
        const jpgFiles = [];

        for (let i = 0; i < frames.length; i++) {
          this.processingProgress = `converting frame ${i + 1} of ${frames.length}...`;
          this.$forceUpdate();

          const frame = frames[i];

          // Handle disposal method from previous frame
          if (i > 0) {
            const prevFrame = frames[i - 1];
            if (prevFrame.disposalType === 2) {
              // Restore to background — fill with white instead of transparent
              tempCtx.fillStyle = "#FFFFFF";
              tempCtx.fillRect(prevFrame.dims.left, prevFrame.dims.top, prevFrame.dims.width, prevFrame.dims.height);
            }
          }

          // Draw the current frame patch onto the compositing canvas
          const frameImageData = new ImageData(
            new Uint8ClampedArray(frame.patch),
            frame.dims.width,
            frame.dims.height
          );
          const frameCanvas = document.createElement("canvas");
          frameCanvas.width = frame.dims.width;
          frameCanvas.height = frame.dims.height;
          frameCanvas.getContext("2d").putImageData(frameImageData, 0, 0);
          tempCtx.drawImage(frameCanvas, frame.dims.left, frame.dims.top);

          // Fill output with white before drawing (handle any remaining transparency)
          outCtx.fillStyle = "#FFFFFF";
          outCtx.fillRect(0, 0, tw, th);
          // Resize (cover) + center-crop to target dimensions
          this.drawCoverCrop(tempCanvas, outCtx, tw, th);

          // First frame: find the right quality; subsequent frames: reuse it
          if (decidedQuality === null) {
            const result = await this.canvasToConstrainedBlob(outCanvas, INITIAL_QUALITY);
            decidedQuality = result.quality;
            this.finalQuality = Math.round(decidedQuality * 100);
            const paddedIndex = String(i).padStart(5, "0");
            jpgFiles.push(new File([result.blob], `frame_${paddedIndex}.jpg`, { type: "image/jpeg" }));
          } else {
            const blob = await new Promise((resolve) =>
              outCanvas.toBlob(resolve, "image/jpeg", decidedQuality)
            );
            const paddedIndex = String(i).padStart(5, "0");
            jpgFiles.push(new File([blob], `frame_${paddedIndex}.jpg`, { type: "image/jpeg" }));
          }

          // Yield to UI thread every 10 frames
          if (i % 10 === 0) {
            await new Promise((r) => setTimeout(r, 0));
          }
        }

        this.extracted_files = jpgFiles;
        // Build preview data URLs from the generated blobs
        this.preview_frames = await Promise.all(
          jpgFiles.map((f) => {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(f);
            });
          })
        );
        this.gif_frames_ready = true;
        this.startPreview();
        this.processingProgress = "done!";
      } catch (e) {
        console.error("GIF processing error:", e);
        this.processingProgress = "Error processing GIF. Please try a different file.";
      } finally {
        this.processing = false;
        this.$forceUpdate();
      }
    },

    uploadAttempt() {
      this.uploading = true;
      this.$forceUpdate();

      const formData = new FormData();
      const filesToSend = this.uploadTab === 0 ? this.extracted_files : this.files_to_upload;

      for (let file of filesToSend) {
        formData.append("screensaver[]", file, file.name);
      }
      formData.append("token", this.device.token);

      this.$store
        .dispatch(UPDATE_SCREENSAVER, formData)
        .then((data) => {
          data;
          this.uploading = false;
          this.gif_file = null;
          this.extracted_files = [];
          this.gif_frames_ready = false;
          this.stopPreview();
          this.preview_frames = [];
          this.files_to_upload = null;
          this.$forceUpdate();
          this.downloadData();
        });
    },

    startPreview() {
      this.stopPreview();
      if (this.preview_frames.length <= 1) return;
      this.previewIndex = 0;
      this.previewTimer = setInterval(() => {
        this.previewIndex = (this.previewIndex + 1) % this.preview_frames.length;
      }, 100);
    },

    stopPreview() {
      if (this.previewTimer) {
        clearInterval(this.previewTimer);
        this.previewTimer = null;
      }
      this.previewIndex = 0;
    },

    downloadData() {
      if (this.device != null) {
        this.$store
          .dispatch(GET_SCREENSAVER, { token: this.device.token })
          .then((data) => {
            if (data.result == "success") {
              this.current_screensaver = data;
            }
            this.$forceUpdate();
          });
      }
    },
  },
  watch: {
    device: function(oldval, newval) {
      oldval, newval;
      this.downloadData();
    },
    gif_file: function(newFile) {
      if (newFile) {
        this.processGif(newFile);
      } else {
        this.extracted_files = [];
        this.gif_frames_ready = false;
        this.stopPreview();
        this.preview_frames = [];
      }
    },
    selectedSize: function() {
      // Re-process if a GIF is already loaded and user changes resolution
      if (this.gif_file) {
        this.processGif(this.gif_file);
      }
    },
  },
  beforeDestroy() {
    this.stopPreview();
  },
};
</script>
